import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { draftKudos, getKudos, sendKudosBatch } from '../api.js';
import {
  FORWARD_SLOTS,
  MESSAGE_MAX,
  MESSAGE_MIN,
  RECIPIENT_EMAIL_MAX,
  RECIPIENT_NAME_MAX,
  SENDER_EMAIL_MAX,
  TRAIT_MAX,
} from '../constants.js';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function makeEmptySlot() {
  return {
    name: '',
    email: '',
    trait: '',
    message: '',
    drafts: [],
    drafting: false,
    draftError: '',
    showHelper: false,
    result: null, // {ok, error?, moderation?} after submit
    sent: false, // true once this slot successfully sent; locked from re-send
  };
}

function isSlotEmpty(slot) {
  if (slot.sent) return true; // sent slots are excluded from validation + submission
  return !slot.name.trim() && !slot.email.trim() && !slot.trait.trim() && !slot.message.trim();
}

function isSlotValid(slot) {
  const name = slot.name.trim();
  const email = slot.email.trim();
  const message = slot.message.trim();
  return (
    name.length > 0 &&
    name.length <= RECIPIENT_NAME_MAX &&
    EMAIL_RE.test(email) &&
    email.length <= RECIPIENT_EMAIL_MAX &&
    message.length >= MESSAGE_MIN &&
    message.length <= MESSAGE_MAX
  );
}

export default function Forward() {
  const { id } = useParams();
  const navigate = useNavigate();
  const turnstileRef = useRef(null);

  const [parent, setParent] = useState(null);
  const [slots, setSlots] = useState(() =>
    Array.from({ length: FORWARD_SLOTS }, makeEmptySlot)
  );
  const [senderEmail, setSenderEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getKudos(id)
      .then((data) => {
        if (!cancelled) setParent(data);
      })
      .catch(() => {
        // non-fatal: we still let them forward without parent context
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const filledSlots = useMemo(() => slots.filter((s) => !isSlotEmpty(s)), [slots]);
  const allFilledValid = filledSlots.length > 0 && filledSlots.every(isSlotValid);
  const senderEmailValid =
    EMAIL_RE.test(senderEmail.trim()) && senderEmail.trim().length <= SENDER_EMAIL_MAX;
  const canSubmit = allFilledValid && senderEmailValid && !!turnstileToken && !submitting;

  function updateSlot(index, patch) {
    setSlots((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  async function handleDraft(index) {
    const slot = slots[index];
    const name = slot.name.trim();
    const trait = slot.trait.trim();
    if (!name || trait.length < 3) {
      updateSlot(index, {
        draftError: 'Add their first name and one true thing about them first.',
      });
      return;
    }
    updateSlot(index, { drafting: true, draftError: '', drafts: [] });
    try {
      const data = await draftKudos({ recipientFirstName: name, trait });
      const drafts = Array.isArray(data?.drafts) ? data.drafts : [];
      updateSlot(index, { drafts, drafting: false });
    } catch (err) {
      updateSlot(index, {
        drafting: false,
        draftError: err.message || 'Couldn\u2019t draft anything just now.',
      });
    }
  }

  function pickDraft(index, draft) {
    updateSlot(index, { message: draft, drafts: [] });
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);

    const items = [];
    const itemSlotIndexes = []; // map back to slot index for per-result attribution
    slots.forEach((s, idx) => {
      if (s.sent || isSlotEmpty(s)) return;
      items.push({
        recipientName: s.name.trim(),
        recipientEmail: s.email.trim(),
        message: s.message.trim(),
      });
      itemSlotIndexes.push(idx);
    });

    if (items.length === 0) {
      setSubmitting(false);
      return;
    }

    try {
      const data = await sendKudosBatch({
        items,
        senderEmail: senderEmail.trim(),
        turnstileToken,
        parentKudosId: id,
      });

      const results = Array.isArray(data?.results) ? data.results : [];
      const sentCount = data?.sentCount || 0;

      // Attach each batch result back to its slot for inline display, and lock
      // any newly-successful slot so a retry doesn't re-send it.
      const nextSlots = slots.map((s, slotIdx) => {
        const itemIdx = itemSlotIndexes.indexOf(slotIdx);
        if (itemIdx === -1) return s;
        const r = results.find((x) => x.slotIndex === itemIdx) || { ok: false, error: 'No response.' };
        if (r.ok) return { ...s, result: r, sent: true };
        return { ...s, result: r };
      });
      setSlots(nextSlots);

      // If nothing is pending anymore (every non-empty slot is sent), navigate.
      const remainingPending = nextSlots.filter((s) => !s.sent && !isSlotEmpty(s));
      const allSent = nextSlots.filter((s) => s.sent);
      if (remainingPending.length === 0 && allSent.length > 0) {
        navigate('/sent', {
          state: {
            recipientNames: allSent.map((s) => s.name.trim()),
            messages: allSent.map((s) => s.message.trim()),
            forwarded: true,
          },
        });
        return;
      }

      // Partial success: keep the user on the page so they can fix the failed slots.
      if (sentCount > 0) {
        setError(
          `${sentCount} of ${items.length} went through. Fix the ones marked below and try again.`
        );
      } else {
        setError('None went through. See the notes below each one.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      turnstileRef.current?.reset?.();
      setTurnstileToken('');
      setSubmitting(false);
    }
  }

  const chainTotal = parent?.chain?.totalCount || 1;
  const chainDepth = parent?.chain?.maxDepth ?? parent?.depth ?? 0;

  return (
    <section className="mt-4 sm:mt-10">
      <div className="text-center mb-8">
        <h1 className="text-4xl sm:text-5xl font-display font-bold text-navy mb-3">
          Pass it on.
        </h1>
        <p className="text-navy-700 text-lg max-w-xl mx-auto">
          Who&rsquo;s someone you know that could use a good word today? Pick up to {FORWARD_SLOTS} &mdash;
          different people, different reasons. We&rsquo;ll help you write each one if you&rsquo;re stuck.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-5 max-w-xl mx-auto">
        {slots.map((slot, i) => (
          <SlotCard
            key={i}
            index={i}
            slot={slot}
            onChange={(patch) => updateSlot(i, patch)}
            onDraft={() => handleDraft(i)}
            onPickDraft={(d) => pickDraft(i, d)}
          />
        ))}

        <p className="text-xs text-navy-500 text-center -mt-2">
          Only filled slots get sent. Leave the rest blank.
        </p>

        <div className="card">
          <label htmlFor="senderEmail" className="label">
            Your email{' '}
            <span className="text-navy-500 font-normal">(just for us &mdash; never shown to them)</span>
          </label>
          <input
            id="senderEmail"
            type="email"
            className="input"
            autoComplete="email"
            maxLength={SENDER_EMAIL_MAX}
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
          <p className="text-xs text-navy-500 mt-1">
            Helps us prevent spam. Never shown to your recipients, never used for marketing.
          </p>
        </div>

        {TURNSTILE_SITE_KEY ? (
          <div className="flex justify-center">
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={setTurnstileToken}
              onExpire={() => setTurnstileToken('')}
              onError={() => setTurnstileToken('')}
              options={{ theme: 'light', appearance: 'interaction-only' }}
            />
          </div>
        ) : (
          <p className="text-xs text-orange-600 text-center">
            Turnstile site key missing &mdash; set <code>VITE_TURNSTILE_SITE_KEY</code>.
          </p>
        )}

        {error && (
          <p className="text-sm text-orange-600 text-center" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="btn-accent w-full" disabled={!canSubmit}>
          {submitting
            ? 'Sending\u2026'
            : `Send ${filledSlots.length || ''}`.trim() + (filledSlots.length === 1 ? ' kudo' : ' kudos')}
        </button>

        <p className="text-xs text-navy-500 text-center">
          Each one is checked for kindness before it leaves. Anonymous to your recipients.
        </p>
      </form>

      <div className="mt-10 max-w-xl mx-auto rounded-2xl bg-teal-50/60 border border-teal-500/20 px-5 py-4 text-sm text-navy-700">
        <div className="flex items-start gap-3">
          <span className="text-xl">🌱</span>
          <div>
            <p className="font-medium text-navy">
              This kindness has reached {chainTotal} {chainTotal === 1 ? 'person' : 'people'}
              {chainDepth > 0 ? ` across ${chainDepth} hand-off${chainDepth === 1 ? '' : 's'}` : ''}.
            </p>
            <p className="text-navy-500 mt-1">
              Yours could be the one that doubles it.{' '}
              <Link to={`/chain/${parent?.rootKudosId || id}`} className="text-teal-700">
                See the chain
              </Link>
              .
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 text-center">
        <Link to={`/k/${id}`} className="text-sm text-navy-500">
          Not feeling it right now? That&rsquo;s okay &mdash; just keep it.
        </Link>
      </div>
    </section>
  );
}

function SlotCard({ index, slot, onChange, onDraft, onPickDraft }) {
  const messageLen = slot.message.trim().length;
  const needsMore = messageLen > 0 && messageLen < MESSAGE_MIN;
  const result = slot.result;

  if (slot.sent) {
    return (
      <div className="card space-y-1 bg-teal-50 border border-teal-200">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-navy">Kudo {index + 1}</h3>
          <span className="text-xs text-teal-700">Sent ✓</span>
        </div>
        <p className="text-sm text-navy-700">
          Delivered to <strong>{slot.name.trim()}</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-navy">Kudo {index + 1}</h3>
        {result?.ok ? (
          <span className="text-xs text-teal-700">Sent ✓</span>
        ) : result && !result.ok ? (
          <span className="text-xs text-orange-600">Needs a fix</span>
        ) : null}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor={`name-${index}`}>
            Who&rsquo;s it for?
          </label>
          <input
            id={`name-${index}`}
            className="input"
            autoComplete="off"
            maxLength={RECIPIENT_NAME_MAX}
            placeholder="e.g. Alex"
            value={slot.name}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </div>
        <div>
          <label className="label" htmlFor={`email-${index}`}>
            Where to?
          </label>
          <input
            id={`email-${index}`}
            type="email"
            className="input"
            autoComplete="off"
            maxLength={RECIPIENT_EMAIL_MAX}
            placeholder="alex@example.com"
            value={slot.email}
            onChange={(e) => onChange({ email: e.target.value })}
          />
        </div>
      </div>

      <div>
        <label className="label" htmlFor={`msg-${index}`}>
          What do you want them to know?
        </label>
        <textarea
          id={`msg-${index}`}
          className="textarea-hero min-h-[120px]"
          maxLength={MESSAGE_MAX}
          placeholder="Something specific and true."
          value={slot.message}
          onChange={(e) => onChange({ message: e.target.value })}
        />
        <div className="flex justify-between text-xs text-navy-500 mt-1">
          <span>{needsMore ? `${MESSAGE_MIN - messageLen} more characters` : ''}</span>
          <span>
            {slot.message.length} / {MESSAGE_MAX}
          </span>
        </div>
        <div className="mt-2">
          <button
            type="button"
            className="text-sm text-teal-700 hover:underline"
            onClick={() => onChange({ showHelper: !slot.showHelper })}
            aria-expanded={!!slot.showHelper}
          >
            {slot.showHelper ? 'Hide writing helper' : '\u2728 Stuck? Help me write it'}
          </button>
        </div>
      </div>

      {slot.showHelper && (
        <div className="rounded-2xl border border-teal-500/30 bg-teal-50/60 p-4 space-y-3">
          <p className="text-xs text-navy-700">
            Tell us one true thing about {slot.name.trim() || 'them'} and we&rsquo;ll suggest three drafts.
          </p>
          <input
            id={`trait-${index}`}
            className="input"
            maxLength={TRAIT_MAX}
            placeholder="e.g. always shows up early, made me laugh when I needed it"
            value={slot.trait}
            onChange={(e) => onChange({ trait: e.target.value })}
          />
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="text-sm font-medium text-teal-700 hover:underline disabled:opacity-50"
              onClick={onDraft}
              disabled={slot.drafting}
            >
              {slot.drafting ? 'Drafting\u2026' : 'Draft three options'}
            </button>
            {slot.draftError && (
              <span className="text-xs text-orange-600">{slot.draftError}</span>
            )}
          </div>
          {slot.drafts.length > 0 && (
            <div className="space-y-2 pt-1">
              <p className="text-xs text-navy-500">Pick one to use it &mdash; you can still edit.</p>
              {slot.drafts.map((d, di) => (
                <button
                  type="button"
                  key={di}
                  className="block w-full text-left rounded-xl border border-teal-500/30 bg-white px-4 py-3 text-sm text-navy hover:bg-teal-50"
                  onClick={() => onPickDraft(d)}
                >
                  {d}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {result && !result.ok && (
        <div className="rounded-xl bg-orange-100/60 border border-orange-500/40 px-4 py-3 text-sm text-navy-900">
          <p className="font-semibold mb-1">
            {result.moderation ? 'This one didn\u2019t feel kind enough.' : 'Couldn\u2019t send this one.'}
          </p>
          <p className="text-navy-700">
            {result.moderation?.reason || result.error || 'Try rephrasing and send again.'}
          </p>
        </div>
      )}
    </div>
  );
}
